import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import jsQR from "jsqr";
import { backend_url } from "../constants/backend";

export default function Point() {
  const location = useLocation();
  const emailFromState = location?.state?.email || "";
  let email = emailFromState;
  if (!email) {
    try {
      email = sessionStorage.getItem("userEmail") || "";
    } catch (err) {
      email = "";
    }
  }

  const [isScanning, setIsScanning] = useState(false);
  const [buttonLocked, setButtonLocked] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [scanError, setScanError] = useState("");
  const [showScanSuccess, setShowScanSuccess] = useState(false); // NEW: Scan success state
  const [popupMessage, setPopupMessage] = useState("");
  const [showPopup, setShowPopup] = useState(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (buttonLocked) {
      const timer = setTimeout(() => setButtonLocked(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [buttonLocked]);

  const showPopupMsg = (msg) => {
    setPopupMessage(msg);
    setShowPopup(true);
    setTimeout(() => setShowPopup(false), 2000);
  };

  const startScan = async () => {
    if (buttonLocked) return;
    setButtonLocked(true);
    setScanError("");
    setManualCode("");
    setShowScanSuccess(false); // Reset scan success
    setIsScanning(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: "environment",
          width: { ideal: 640 },
          height: { ideal: 480 }
        },
        audio: false
      });
      
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.load();
        videoRef.current.play().catch(e => console.log("Play error:", e));
      }
      
      scanIntervalRef.current = setInterval(() => {
        if (!videoRef.current?.videoWidth || !canvasRef.current) return;
        const video = videoRef.current;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, canvas.width, canvas.height);
        if (code?.data) {
          console.log("✅ QR:", code.data);
          setManualCode(code.data);
          stopScan();
          setShowScanSuccess(true); // SHOW SCAN SUCCESS
        }
      }, 500);
      
    } catch (err) {
      console.error("Camera error:", err);
      setScanError("Camera permission denied");
      setIsScanning(false);
      setButtonLocked(false);
    }
  };

  const stopScan = () => {
    setIsScanning(false);
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
      videoRef.current.load();
    }
    setButtonLocked(false);
  };

  const handleScanClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (buttonLocked) return;
    
    if (isScanning) {
      stopScan();
    } else {
      startScan();
    }
  };

  useEffect(() => {
    return () => stopScan();
  }, []);

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanError(""); 
    setManualCode("");
    setShowScanSuccess(false);
    
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        canvas.width = img.width; 
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, canvas.width, canvas.height);
        
        if (code?.data) {
          setManualCode(code.data);
          setShowScanSuccess(true); // SHOW SCAN SUCCESS
        } else {
          setScanError("No QR code found");
        }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleSendPoints = async () => {
    const qr_value = manualCode.trim();
    if (!qr_value) {
      showPopupMsg("Please scan a QR code first");
      return;
    }
    
    // ✅ CLEAR SCAN SUCCESS TEXT ON SUBMIT
    setShowScanSuccess(false);
    
    console.log("Sending to backend:", { qr_code: qr_value, email });
    try {
      const resp = await fetch(`${backend_url}/points-data/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          qr_code: qr_value, 
          email: email || "" 
        }),
      });
      
      let data = {};
      try {
        data = await resp.json();
      } catch (e) {
        data = {};
      }
      
      if (!resp.ok) {
        showPopupMsg(data.detail || data.error || "Error adding points");
        return;
      }
      
      showPopupMsg("🎉 Points Added Successfully!");
      setManualCode(""); // CLEAR QR
      
    } catch (err) {
      console.error("Network error:", err);
      showPopupMsg("Network error");
    }
  };

  return (
    <div className="react-background" style={{
      height: "100vh", overflow: "hidden", color: "white",
      display: "flex", justifyContent: "center", alignItems: "center"
    }}>
      <div style={{ width: 350, borderRadius: 16 }}>
        <h2 style={{ textAlign: "center" }}>Games Points</h2>

        <div style={{
          marginTop: 10,
          padding: 12,
          borderRadius: 12,
          border: "1px dashed rgba(148,163,184,0.7)"
        }}>
          <div style={{ display: "flex", gap: 8 }}>
            <button 
              onClick={handleScanClick}
              onTouchStart={handleScanClick}
              disabled={buttonLocked}
              style={{
                flex: 1,
                padding: "14px 10px",
                borderRadius: 8,
                border: "none",
                background: buttonLocked 
                  ? "#64748b" 
                  : "linear-gradient(90deg, rgb(99,102,241), rgb(34,197,94))",
                color: "white",
                cursor: buttonLocked ? "not-allowed" : "pointer",
                fontSize: 20,
                opacity: buttonLocked ? 0.7 : 1
              }}
            >
              Scan QR
            </button>
            <button 
              onClick={() => fileInputRef.current?.click()}
              style={{
                width: 50,
                borderRadius: 8,
                border: "none",
                background: "#1f2937",
                color: "white",
                cursor: "pointer"
              }}
            >
              🖼
            </button>
            <input type="file" accept="image/*" ref={fileInputRef} 
                   style={{ display: "none" }} onChange={handleImageUpload} />
          </div>

          {scanError && (
            <p style={{ marginTop: 8, color: "#f87171" }}>{scanError}</p>
          )}

          {isScanning && (
            <div style={{ marginTop: 10 }}>
              <video 
                ref={videoRef} 
                style={{ 
                  width: "100%", 
                  height: 240, 
                  objectFit: "cover", 
                  borderRadius: 10,
                  backgroundColor: "#000",
                  display: "block"
                }} 
                autoPlay 
                muted 
                playsInline
              />
            </div>
          )}

          <canvas ref={canvasRef} style={{ display: "none" }} />

          {/* SCAN SUCCESS TEXT - CLEARS ON SUBMIT */}
          {showScanSuccess && (
            <div style={{
              marginTop: 12,
              padding: "10px 12px",
              background: "rgba(22, 163, 74, 0.2)",
              borderRadius: 10,
              textAlign: "center",
              fontSize: 16,
              fontWeight: 600,
              color: "#16a34a",
              border: "1px solid rgba(22, 163, 74, 0.3)"
            }}>
              ✅ QR scanned successfully!
            </div>
          )}
        </div>

        {/* ALWAYS SHOW SUBMIT BUTTON */}
        <button 
          onClick={handleSendPoints}
          style={{
            marginTop: 12,
            padding: "14px 14px",
            width: "100%",
            borderRadius: 8,
            background: "linear-gradient(90deg, rgb(99,102,241), rgb(34,197,94))",
            border: "none",
            cursor: "pointer",
            color: "white",
            fontWeight: 600,
            fontSize: 20
          }}
        >
          Submit Point
        </button>
      </div>

      {showPopup && (
        <div style={{
          position: "fixed", bottom: 30, left: "50%", transform: "translateX(-50%)",
          padding: "12px 20px", background: "#16a34a", color: "white",
          borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
          fontSize: 15, fontWeight: "bold", zIndex: 9999
        }}>
          {popupMessage}
        </div>
      )}
    </div>
  );
}
